import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/sonner";
import { FormField, isFieldValid } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

export function EditMemberModal({ member, open, onOpenChange }) {
  const [name, setName] = useState(member?.profile?.name || "");
  const [email, setEmail] = useState(member?.profile?.email || "");
  const [note, setNote] = useState("");
  const queryClient = useQueryClient();
  const [success, pulse] = useSuccessPulse();
  const formValid = isFieldValid("text", true, name) && isFieldValid("email", false, email);

  const mutation = useMutation({
    mutationFn: () => api.patch(`/members/${member.id}`, { name, email: email || null, note: note || null }),
    onSuccess: () => pulse(() => {
      toast.success("Member updated.");
      queryClient.invalidateQueries({ queryKey: ["desk-member", member.id] });
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal">
        <DialogHeader>
          <DialogTitle>Edit {member?.profile?.name}</DialogTitle>
          <DialogDescription>Update contact details or add a note to their file.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Name" required testId={DESK.editNameInput} value={name} onChange={setName} />
          <FormField label="Email" type="email" testId={DESK.editEmailInput} value={email} onChange={setEmail} helperText="Optional" />
          <div className="space-y-1.5">
            <Label>Add a note (optional)</Label>
            <Textarea data-testid={DESK.editNoteInput} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <SubmitButton
            testId={DESK.editSaveButton} pending={mutation.isPending} success={success} disabled={!formValid}
            onClick={() => mutation.mutate()} label="Save" successLabel="Saved"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
